import React, { useCallback, useState } from "react";
import { Tag } from "primereact/tag";
import { Divider } from "primereact/divider";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";

import { updateEvseLabel } from "../../../../../services/clearing/stations";

const getStatusSeverity = (
  status: string,
): "success" | "info" | "danger" | "warning" => {
  switch (status) {
    case "AVAILABLE":
      return "success";
    case "CHARGING":
      return "info";
    case "UNAVAILABLE":
      return "danger";
    default:
      return "warning";
  }
};

const EvseNameForm = ({
  stationId = "",
  evseId = "",
  currentLabel = "",
}: {
  stationId: string;
  evseId: string;
  currentLabel: string;
}) => {
  const [label, setLabel] = useState("");

  const handleOnSubmit = useCallback(
    async (params: { evseId: string; label: string; stationId: string }) => {
      const success = await updateEvseLabel(params);
      if (success) {
        window.alert("EVSE label updated successfully!");
      } else {
        window.alert("Failed to update EVSE label, please try again later!");
      }
    },
    [],
  );

  return (
    <div className="flex align-items-center gap-2">
      <InputText
        style={{ height: "32px" }}
        value={label}
        onChange={(e) => setLabel(e.currentTarget.value)}
        placeholder={currentLabel || "Add label"}
      />
      <Button
        onClick={() => handleOnSubmit({ evseId, label, stationId })}
        style={{ height: "32px" }}
        label="Change"
        size="small"
      />
    </div>
  );
};

export default function EvseList({
  evses,
  stationId,
}: {
  evses: any[];
  stationId: string;
}) {
  if (!evses || evses.length === 0) {
    return <p className="text-color-secondary">No EVSEs available.</p>;
  }

  return (
    <div>
      {evses.map((evse, evseIdx) => (
        <div key={evse.evse_id}>
          {evseIdx > 0 && <Divider />}

          <div className="flex flex-column md:flex-row justify-content-between align-items-start md:align-items-center mb-3 gap-2">
            <div>
              <div className="text-xs text-color-secondary mb-1">EVSE ID</div>
              <div className="font-semibold text-lg">{evse.evse_id}</div>
              <Tag
                value={evse.status}
                severity={getStatusSeverity(evse.status)}
                className="mt-2"
              />
            </div>
            <EvseNameForm
              evseId={evse.evse_id}
              stationId={stationId}
              currentLabel={evse.label}
            />
          </div>

          {evse.connectors?.length > 0 && (
            <>
              <div className="text-sm font-semibold text-color-secondary mb-2">
                Connectors
              </div>
              <div className="flex flex-column gap-2 mb-3">
                {evse.connectors.map((connector: any, idx: number) => (
                  <div
                    key={idx}
                    className="flex flex-wrap gap-4 p-2 border-round"
                    style={{ background: "var(--surface-ground)" }}
                  >
                    <div>
                      <span className="text-xs text-color-secondary block mb-1">ID</span>
                      <span className="text-sm font-medium">{connector.id}</span>
                    </div>
                    <div>
                      <span className="text-xs text-color-secondary block mb-1">Type</span>
                      <span className="text-sm font-medium">{connector.power_type}</span>
                    </div>
                    <div>
                      <span className="text-xs text-color-secondary block mb-1">Max Power</span>
                      <span className="text-sm font-medium">{connector.max_electric_power} kW</span>
                    </div>
                    <div>
                      <span className="text-xs text-color-secondary block mb-1">Standard</span>
                      <span className="text-sm font-medium">{connector.standard}</span>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {evse.capabilities?.length > 0 && (
            <>
              <div className="text-sm font-semibold text-color-secondary mb-2">
                Capabilities
              </div>
              <div className="flex flex-wrap gap-2">
                {evse.capabilities.map((cap: string, idx: number) => (
                  <Tag key={idx} value={cap} severity="info" />
                ))}
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
}
